import React, { useState } from 'react';
import { SITE_CONFIG, trackEvent } from '../config/site';
import { Menu, X, ArrowRight, LogIn } from 'lucide-react';

interface NavbarProps {
  onOpenWaitlist: () => void;
}

export const Navbar: React.FC<NavbarProps> = ({ onOpenWaitlist }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLoginClick = () => {
    trackEvent('login_click', { location: 'navbar' });
  };

  const handleCtaClick = () => {
    trackEvent('hero_cta', { location: 'navbar' });
    onOpenWaitlist();
    setMobileMenuOpen(false);
  };

  return (
    <header className="sticky top-0 z-40 w-full backdrop-blur-md bg-brand-bg/85 border-b border-brand-border transition-all">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
        {/* Brand Logo */}
        <a href="#" className="flex items-center gap-3 group focus:outline-none focus:ring-2 focus:ring-brand-primary rounded-lg p-1">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-primary via-brand-secondary to-brand-cyan flex items-center justify-center shadow-lg shadow-brand-primary/20 group-hover:scale-105 transition-transform">
            <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </svg>
          </div>
          <div className="flex flex-col">
            <span className="font-heading font-bold text-xl tracking-tight text-white flex items-center gap-1.5">
              InfoForge
              <span className="text-[10px] font-semibold tracking-wider uppercase px-2 py-0.5 rounded-full bg-brand-primary/20 text-brand-cyan border border-brand-cyan/30">
                Beta
              </span>
            </span>
            <span className="text-[10px] text-brand-textSubtle uppercase tracking-wider font-medium">
              by DB8 Intelligence
            </span>
          </div>
        </a>

        {/* Desktop Navigation Links */}
        <nav className="hidden md:flex items-center gap-8 text-sm font-medium text-brand-textMuted">
          <a href="#como-funciona" className="hover:text-white transition-colors">Como Funciona</a>
          <a href="#entregaveis" className="hover:text-white transition-colors">Entregáveis</a>
          <a href="#consistencia" className="hover:text-white transition-colors">Consistência</a>
          <a href="#planos" className="hover:text-white transition-colors">Planos</a>
          <a href="#faq" className="hover:text-white transition-colors">FAQ</a>
        </nav>

        {/* Desktop Actions */}
        <div className="hidden md:flex items-center gap-4">
          <a
            href={SITE_CONFIG.loginUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleLoginClick}
            className="flex items-center gap-2 text-sm font-medium text-brand-textMuted hover:text-white px-4 py-2 rounded-lg hover:bg-white/5 transition-all"
          >
            <LogIn className="w-4 h-4" />
            Entrar
          </a>
          <button
            onClick={handleCtaClick}
            className="flex items-center gap-2 text-sm font-semibold text-white bg-gradient-to-r from-brand-primary to-brand-secondary hover:opacity-95 px-5 py-2.5 rounded-xl shadow-lg shadow-brand-primary/25 hover:shadow-brand-primary/40 transition-all hover:scale-[1.02] active:scale-[0.98]"
          >
            Solicitar Acesso
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>

        {/* Mobile Menu Toggle Button */}
        <div className="flex md:hidden items-center gap-2">
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="p-2 rounded-lg text-brand-textMuted hover:text-white hover:bg-white/5 focus:outline-none focus:ring-2 focus:ring-brand-primary"
            aria-label="Abrir menu de navegação"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Drawer */}
      {mobileMenuOpen && (
        <div className="md:hidden border-b border-brand-border bg-brand-surface/95 backdrop-blur-xl px-4 pt-4 pb-6 space-y-4">
          <nav className="flex flex-col space-y-3 text-base font-medium text-brand-textMuted">
            <a href="#como-funciona" onClick={() => setMobileMenuOpen(false)} className="hover:text-white p-2">Como Funciona</a>
            <a href="#entregaveis" onClick={() => setMobileMenuOpen(false)} className="hover:text-white p-2">Entregáveis</a>
            <a href="#consistencia" onClick={() => setMobileMenuOpen(false)} className="hover:text-white p-2">Consistência</a>
            <a href="#planos" onClick={() => setMobileMenuOpen(false)} className="hover:text-white p-2">Planos</a>
            <a href="#faq" onClick={() => setMobileMenuOpen(false)} className="hover:text-white p-2">FAQ</a>
          </nav>
          <div className="pt-4 border-t border-brand-border flex flex-col gap-3">
            <a
              href={SITE_CONFIG.loginUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleLoginClick}
              className="flex items-center justify-center gap-2 text-sm font-medium text-brand-text py-2.5 rounded-xl border border-brand-border bg-brand-glass"
            >
              <LogIn className="w-4 h-4" />
              Já tenho convite (Entrar)
            </a>
            <button
              onClick={handleCtaClick}
              className="flex items-center justify-center gap-2 text-sm font-semibold text-white bg-gradient-to-r from-brand-primary to-brand-secondary py-3 rounded-xl shadow-md"
            >
              Solicitar Acesso ao Private Beta
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </header>
  );
};
